import BaseDialog from 'common/components/base-dialog';
import React, { useEffect } from 'react';
import LogicSkipQuestion from './logic-skip-question';
import BrandLogo from 'common/components/brand-logo';
import LogicCreeningQuestion from './logic-creening-question';
import LogicOnlyDisplaySelectedChoice from './logic-only-display-selected-choice';
import LogicDisplayOnTitle from './logic-display-on-title-question';
import LogicBranchQuestion from './logic-branch-question';
import { LogicQuestionType } from 'pages/admin/campaign-overview/constants';
import {
    skipLogicOptions,
    creeningLogicOptions,
    answerFromOtherQuestionOptions,
    branchLogicOptions,
} from 'services/campaign.service';
import { useParams } from 'react-router-dom';
import { IQuestionNewEditForm } from 'types/campaign-design';
import { handleError } from 'utils/helpers';
import { toast } from 'react-toastify';

interface ModalQuestionLogicProps {
    logicType: string;
    open: boolean;
    onClose: () => void;
    question: IQuestionNewEditForm;
    replaceQuestionByQuestionId: (
        questionId: number,
        question: IQuestionNewEditForm
    ) => void;
}

const ModalQuestionLogic: React.FC<ModalQuestionLogicProps> = ({
    logicType,
    open,
    onClose,
    question,
    replaceQuestionByQuestionId,
}) => {
    const currentCampaignId = Number(useParams().id);
    const [logicOptions, setOptions] = React.useState<any>({}); // eslint-disable-line @typescript-eslint/no-explicit-any
    const [logicValues, setLogicValues] = React.useState<any>({}); // eslint-disable-line @typescript-eslint/no-explicit-any
    const [trueOpenReady, setTrueOpenReady] = React.useState(false);

    useEffect(() => {
        const fetchskipLogicOptions = async () => {
            try {
                const response = await skipLogicOptions(
                    currentCampaignId,
                    Number(question.id)
                );
                if (
                    response.success &&
                    response.data?.options &&
                    response.data?.values
                ) {
                    setOptions(response.data?.options);
                    setLogicValues(response.data?.values);
                    setTrueOpenReady(true);
                } else {
                    toast.error(response.message);
                    setTrueOpenReady(false);
                    onClose();
                }
            } catch (error) {
                handleError(error);
                setTrueOpenReady(false);
                onClose();
            }
        };

        const fetchCreeningLogicOptions = async () => {
            try {
                const response = await creeningLogicOptions(
                    currentCampaignId,
                    Number(question.id)
                );
                if (
                    response.success &&
                    response.data?.options &&
                    response.data?.values
                ) {
                    setOptions(response.data?.options);
                    setLogicValues(response.data?.values);
                    setTrueOpenReady(true);
                } else {
                    toast.error(response.message);
                    setTrueOpenReady(false);
                    onClose();
                }
            } catch (error) {
                handleError(error);
                setTrueOpenReady(false);
                onClose();
            }
        };

        const fetchAnswerFromOtherQuestionOptions = async () => {
            try {
                const response = await answerFromOtherQuestionOptions(
                    currentCampaignId,
                    Number(question.id)
                );
                if (response.success && response.data?.options) {
                    setOptions(response.data?.options);
                    setLogicValues(response.data?.value);
                    setTrueOpenReady(true);
                } else {
                    toast.error(response.message);
                    setTrueOpenReady(false);
                    onClose();
                }
            } catch (error) {
                handleError(error);
                setTrueOpenReady(false);
                onClose();
            }
        };
        const fetchBranchLogicOptions = async () => {
            try {
                const response = await branchLogicOptions(
                    currentCampaignId,
                    Number(question.id)
                );
                if (
                    response.success &&
                    response.data?.options &&
                    response.data?.values
                ) {
                    setOptions(response.data?.options);
                    setLogicValues(response.data?.values);
                    setTrueOpenReady(true);
                } else {
                    toast.error(response.message);
                    setTrueOpenReady(false);
                    onClose();
                }
            } catch (error) {
                handleError(error);
                setTrueOpenReady(false);
                onClose();
            }
        };
        if (open) {
            switch (logicType) {
                case LogicQuestionType.Skip: {
                    fetchskipLogicOptions();
                    break;
                }
                case LogicQuestionType.Branch: {
                    fetchBranchLogicOptions();
                    break;
                }
                case LogicQuestionType.Creening: {
                    fetchCreeningLogicOptions();
                    break;
                }
                case LogicQuestionType.OnlyDisplaySelectedChoice: {
                    fetchAnswerFromOtherQuestionOptions();
                    break;
                }
                case LogicQuestionType.DisplayOnTitle:
                    break;
                default:
                    break;
            }
        }
    }, [open, logicType, currentCampaignId, question.id]);

    const generateLogic = () => {
        switch (logicType) {
            case LogicQuestionType.Skip:
                return (
                    <LogicSkipQuestion
                        onClose={onClose}
                        options={logicOptions}
                        values={logicValues}
                        targetQuestionId={question.id}
                        replaceQuestionByQuestionId={
                            replaceQuestionByQuestionId
                        }
                    />
                );
            case LogicQuestionType.Creening:
                return (
                    <LogicCreeningQuestion
                        onClose={onClose}
                        options={logicOptions}
                        values={logicValues}
                        targetQuestionId={question.id}
                        replaceQuestionByQuestionId={
                            replaceQuestionByQuestionId
                        }
                    />
                );
            case LogicQuestionType.Branch:
                return (
                    <LogicBranchQuestion
                        question={question}
                        onClose={onClose}
                        options={logicOptions}
                        values={logicValues}
                        replaceQuestionByQuestionId={
                            replaceQuestionByQuestionId
                        }
                    />
                );
            case LogicQuestionType.OnlyDisplaySelectedChoice:
                return (
                    <LogicOnlyDisplaySelectedChoice
                        onClose={onClose}
                        question={question}
                        options={logicOptions}
                        value={logicValues}
                        replaceQuestionByQuestionId={
                            replaceQuestionByQuestionId
                        }
                    />
                );
            case LogicQuestionType.DisplayOnTitle:
                return <LogicDisplayOnTitle onClose={onClose} />;
            default:
                return <LogicDisplayOnTitle onClose={onClose} />;
        }
    };
    return (
        <BaseDialog
            title=""
            open={open && trueOpenReady}
            fullWidth={false}
            maxWidth={false}
            sxContent={{
                width: '902px',
                padding: '45px',
            }}
            onClose={() => onClose()}
        >
            <BrandLogo width={90} style={{ marginBottom: '30px' }} />
            {generateLogic()}
        </BaseDialog>
    );
};

export default ModalQuestionLogic;
